/**
 * API Service Layer for BOM Extractor
 * Handles all backend communication
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  FileUploadResponse,
  ExtractionResponse,
  ExtractionJob,
  CacheStats,
  KnownExtraction,
  FileListResponse,
  BOMItem,
  Component,
  Manufacturer,
  PDFMetadata,
  Dimension,
  ExtractionRule,
  CreateRuleRequest,
  CustomerFolder,
  GroupedRulesResponse,
} from '../types/bom.types';

// Create axios instance with base configuration
const API_BASE = process.env.REACT_APP_API_BASE || 
  (window.location.hostname === 'localhost' ? 'http://localhost:8000' : window.location.origin);

const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 120000, // 2 minutes for large file uploads
  headers: {
    'Content-Type': 'application/json',
  },
});

// Error handler
const handleApiError = (error: AxiosError<{ message?: string; detail?: unknown }>): never => {
  if (error.response) {
    const data = error.response.data;
    const detail = typeof data?.detail === 'string' ? data.detail.split('\n')[0] : undefined;
    throw new Error(data?.message || detail || error.response.statusText);
  } else if (error.request) {
    // Request made but no response
    throw new Error('No response from server. Please check your connection.');
  } else {
    // Request setup error
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// ============================================================================
// File Upload APIs
// ============================================================================

export const uploadPDF = async (file: File, folderId?: string | null): Promise<FileUploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (folderId) {
      formData.append('folder_id', folderId);
    }
    
    const response = await api.post<FileUploadResponse>('/api/upload/pdf', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const uploadExcel = async (file: File): Promise<FileUploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post<FileUploadResponse>('/api/upload/xls', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const listFiles = async (): Promise<FileListResponse> => {
  try {
    const response = await api.get<FileListResponse>('/api/files/list');
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

// ============================================================================
// Customer Folder APIs
// ============================================================================

export const listFolders = async (): Promise<CustomerFolder[]> => {
  try {
    const response = await api.get<CustomerFolder[]>('/api/folders');
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const getComponentTypes = async (): Promise<{ name: string; display_name: string }[]> => {
  const defaultTypes = [
    { name: 'MCB', display_name: 'Circuit Breaker' },
    { name: 'CONTACTOR', display_name: 'Contactor' },
    { name: 'RELAY', display_name: 'Relay' },
    { name: 'TERMINAL', display_name: 'Terminal' },
    { name: 'FUSE', display_name: 'Fuse' },
    { name: 'MOTOR', display_name: 'Motor' },
    { name: 'PLC', display_name: 'PLC' },
    { name: 'OTHER', display_name: 'Other' },
  ];
  
  try {
    const response = await api.get<{ success: boolean; types: { name: string; display_name: string }[] }>('/api/component-types/list');
    // API returns {success: true, types: [...]}
    if (response.data?.success && Array.isArray(response.data.types)) {
      return response.data.types;
    }
    // Fallback if response format is unexpected
    if (Array.isArray(response.data)) {
      return response.data;
    }
    return defaultTypes;
  } catch (error) {
    // Return default types if API fails
    console.error('Failed to load component types:', error);
    return defaultTypes;
  }
};

export const createFolder = async (customerName: string, description?: string): Promise<CustomerFolder> => {
  try {
    const response = await api.post<CustomerFolder>('/api/folders', {
      customer_name: customerName,
      description: description || '',
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const getFolder = async (folderId: string): Promise<CustomerFolder> => {
  try {
    const response = await api.get<CustomerFolder>(`/api/folders/${folderId}`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const updateFolder = async (folderId: string, description: string): Promise<CustomerFolder> => {
  try {
    const response = await api.put<CustomerFolder>(`/api/folders/${folderId}`, {
      description,
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const deleteFolder = async (folderId: string): Promise<{ message: string }> => {
  try {
    const response = await api.delete<{ message: string }>(`/api/folders/${folderId}`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const assignFilesToFolder = async (folderId: string, fileIds: string[]): Promise<{ message: string }> => {
  try {
    const response = await api.post<{ message: string }>(`/api/folders/${folderId}/files`, {
      file_ids: fileIds,
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const removeFilesFromFolder = async (folderId: string, fileIds: string[]): Promise<{ message: string }> => {
  try {
    const response = await api.delete<{ message: string }>(`/api/folders/${folderId}/files`, {
      data: { file_ids: fileIds },
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const getRulesGrouped = async (): Promise<GroupedRulesResponse> => {
  try {
    const response = await api.get<GroupedRulesResponse>('/api/rules?grouped=true');
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

// ============================================================================
// Extraction APIs
// ============================================================================

/**
 * Extract BOM using inference engine (NEW - recommended)
 * 
 * Uploads PDF and immediately returns extracted BOM with:
 * - Main BOM items with confidence scores
 * - Auto-generated auxiliary items (DIN rails, markers, etc.)
 * - Extraction statistics
 */
export const extractWithInference = async (
  file: File,
  useLLM: boolean = false
): Promise<{
  success: boolean;
  filename: string;
  main_bom: any[];
  auxiliary_bom: any[];
  stats: {
    main_items: number;
    auxiliary_items: number;
    avg_confidence: number;
    low_confidence_count: number;
    high_confidence_count: number;
    confidence_breakdown: {
      high: number;
      medium: number;
      low: number;
    };
  };
  extraction_method: string;
  kb_version: string;
}> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('use_llm', useLLM.toString());
    
    const response = await api.post('/api/extract/inference', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * Get inference engine statistics
 */
export const getInferenceStats = async (): Promise<{
  inference_available: boolean;
  stats: any;
}> => {
  try {
    const response = await api.get('/api/inference/stats');
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

// ============================================================================
// New UI Integration APIs
// ============================================================================

export const extractAllPages = async (
  file: File | null,
  useLLM: boolean = false,
  onProgress?: (progress: { message: string; progress: number; status: string }) => void,
  onComplete?: (result: any) => void,
  onError?: (error: string) => void,
  filename?: string | null,  // Optional: if provided, use already-uploaded file instead of uploading
  folderId?: string | null,  // Optional: folder ID for customer-specific rules
  selectedPages?: number[] | null  // Optional: specific page numbers to extract
): Promise<{
  success: boolean;
  filename: string;
  main_bom: any[];
  auxiliary_bom: any[];
  stats: any;
  extraction_method: string;
  kb_version: string;
  // Batch extraction fields (optional - only present for large PDFs)
  next_batch?: { start_page: number; end_page: number; remaining_pages: number } | null;
  total_pages?: number | null;
  pages_processed?: number | null;
  page_range?: { start: number; end: number } | null;
}> => {
  return new Promise((resolve, reject) => {
    try {
      const formData = new FormData();
      
      // If filename is provided, use already-uploaded file (skip re-upload)
      // Check filename first (preferred method to avoid re-upload)
      if (filename && filename.trim() !== '') {
        formData.append('filename', filename);
        console.log(`📤 Using already-uploaded file: ${filename}`);
      } else if (file) {
        // Fallback to file upload if no filename provided
        formData.append('file', file);
        console.log(`📤 Uploading file: ${file.name} (filename was: ${filename || 'null/undefined'})`);
      } else {
        console.error(`❌ extractAllPages: Neither filename nor file provided. filename=${filename}, file=${file}`);
        reject(new Error('Either file or filename must be provided'));
        return;
      }
      
      formData.append('use_llm', useLLM.toString());
      
      // Add folder ID for customer-specific rules
      if (folderId) {
        formData.append('folder_id', folderId);
        console.log(`📁 Using folder-specific rules: ${folderId}`);
      }
      
      // Add selected pages if specified
      if (selectedPages && selectedPages.length > 0) {
        formData.append('selected_pages', selectedPages.join(','));
        console.log(`📄 Extracting selected pages: ${selectedPages.join(', ')}`);
      }
      
      // Use EventSource for Server-Sent Events
      // Since EventSource doesn't support POST, we'll use fetch with streaming
      fetch(`${api.defaults.baseURL}/api/extract/all`, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header, browser will set it with boundary
      }).then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        
        if (!reader) {
          throw new Error('No response body');
        }
        
        let buffer = '';
        
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }
          
          buffer += decoder.decode(value, { stream: true });
          
          // Process complete SSE messages
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                if (data.type === 'progress') {
                  // Update progress
                  if (onProgress) {
                    onProgress({
                      message: data.message || '',
                      progress: data.progress || 0,
                      status: data.status || 'processing'
                    });
                  }
                } else if (data.type === 'complete') {
                  // Extraction complete
                  if (onComplete) {
                    onComplete(data);
                  }
                  resolve({
                    success: data.success,
                    filename: data.filename,
                    main_bom: data.main_bom || [],
                    auxiliary_bom: data.auxiliary_bom || [],
                    stats: data.stats || {},
                    extraction_method: data.extraction_method || 'unknown',
                    kb_version: data.kb_version || 'unknown',
                    // Batch extraction fields
                    next_batch: data.next_batch || null,
                    total_pages: data.total_pages || null,
                    pages_processed: data.pages_processed || null,
                    page_range: data.page_range || null
                  });
                  return;
                } else if (data.type === 'error') {
                  // Error occurred
                  const errorMsg = data.error || 'Unknown error';
                  if (onError) {
                    onError(errorMsg);
                  }
                  reject(new Error(errorMsg));
                  return;
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e, line);
              }
            }
          }
        }
        
        // If we get here without completing, something went wrong
        reject(new Error('Stream ended without completion'));
      }).catch((error) => {
        if (onError) {
          onError(error.message);
        }
        reject(error);
      });
    } catch (error) {
      const err = error as Error;
      if (onError) {
        onError(err.message);
      }
      reject(err);
    }
  });
};

export const matchErpParts = async (bomData: {
  main_bom: any[];
  auxiliary_bom: any[];
}): Promise<{
  success: boolean;
  main_bom: any[];
  auxiliary_bom: any[];
  matched_count: number;
}> => {
  try {
    const response = await api.post('/api/erp/match', bomData);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const exportToExcel = async (bomData: {
  main_bom: any[];
  auxiliary_bom: any[];
}): Promise<Blob> => {
  try {
    const response = await api.post('/api/export/excel', bomData, {
      responseType: 'blob',
    });
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const comparePage = async (
  file: File,
  pageNumber: number,
  llmModel: string = 'claude'
): Promise<{
  success: boolean;
  page_number: number;
  text_extraction: {
    items: any[];
    count: number;
    method: string;
  };
  llm_extraction: {
    items: any[];
    count: number;
    method: string;
    model: string;
  };
}> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('page_number', pageNumber.toString());
    formData.append('llm_model', llmModel);
    
    const response = await api.post('/api/compare/page', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const extractPage = async (
  pdfFilename: string,
  pageNumber: number,
  model: string,
  forceReextract: boolean = false
): Promise<ExtractionResponse> => {
  try {
    const params = forceReextract ? { force_reextract: 'true' } : {};
    
    const response = await api.post<ExtractionResponse>(
      '/api/extract/page',
      {
        pdf_filename: pdfFilename,
        page_number: pageNumber,
        model: model,
      },
      { params }
    );
    
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const finalizeExtraction = async (
  pdfFilename: string,
  xlsFilename: string | null,
  items: any[],
  model: string,
  folderId?: string | null
): Promise<{ job_id: string }> => {
  try {
    const response = await api.post<{ job_id: string }>('/api/extract/finalize', {
      pdf_filename: pdfFilename,
      xls_filename: xlsFilename,
      items: items,
      model: model,
      folder_id: folderId || null,
    });
    
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

// ============================================================================
// Job Management APIs
// ============================================================================

export const getJobs = async (): Promise<ExtractionJob[]> => {
  try {
    const response = await api.get<ExtractionJob[]>('/api/jobs');
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const getJob = async (jobId: string): Promise<ExtractionJob> => {
  try {
    const response = await api.get<ExtractionJob>(`/api/jobs/${jobId}`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const getJobExtractions = async (jobId: string): Promise<any[]> => {
  try {
    const response = await api.get<any[]>(`/api/jobs/${jobId}/extractions`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const deleteJob = async (jobId: string): Promise<void> => {
  try {
    await api.delete(`/api/jobs/${jobId}`);
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

// ============================================================================
// Cache Management APIs
// ============================================================================

export const clearPdfCache = async (pdfFilename: string): Promise<{ message: string }> => {
  try {
    const response = await api.delete<{ message: string }>(`/api/cache/pdf/${pdfFilename}`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const clearPageCache = async (
  pdfFilename: string,
  pageNumber: number,
  model: string
): Promise<{ message: string }> => {
  try {
    const response = await api.delete<{ message: string }>(
      `/api/cache/page/${pdfFilename}/${pageNumber}`,
      { params: { model } }
    );
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const getCacheStats = async (pdfFilename: string): Promise<CacheStats> => {
  try {
    const response = await api.get<CacheStats>('/api/cache/stats', {
      params: { pdf_filename: pdfFilename },
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export interface CachedExtractionData {
  pdf_filename: string;
  total_pages: number;
  cached_pages: number;
  items: BOMItem[];
  components: Component[];
  manufacturers: Manufacturer[];
  metadata: PDFMetadata | null;
  features: any[];
  dimensions: Dimension[];
  _from_cache: boolean;
}

export const loadCachedExtraction = async (
  pdfFilename: string,
  model: string = 'gpt4o'
): Promise<CachedExtractionData> => {
  try {
    const response = await api.get<CachedExtractionData>(
      `/api/cache/load/${pdfFilename}`,
      { params: { model } }
    );
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

// ============================================================================
// Known Extractions APIs
// ============================================================================

export const getKnownExtractions = async (): Promise<{
  total: number;
  known_extractions: KnownExtraction[];
}> => {
  try {
    const response = await api.get<{
      total: number;
      known_extractions: KnownExtraction[];
    }>('/api/extractions/known');
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

// ============================================================================
// Extraction History APIs
// ============================================================================

export interface ExtractionHistoryItem {
  job_id: string;
  pdf_filename: string;
  xls_filename?: string | null;
  pdf_url: string;
  xls_url?: string | null;
  model: string;
  total_candidates?: number;
  auto_accept?: number;
  review?: number;
  avg_confidence?: number;
  notes?: string | null;
  created_at: string;
  completed_at?: string | null;
  has_extraction_data: boolean;
}

export interface ExtractionHistoryResponse {
  success: boolean;
  history: ExtractionHistoryItem[];
  total: number;
}

export const getExtractionHistory = async (): Promise<ExtractionHistoryResponse> => {
  try {
    const response = await api.get<ExtractionHistoryResponse>('/api/extractions/history');
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export interface LoadExtractionResponse {
  success: boolean;
  job_id: string;
  pdf_filename: string;
  xls_filename?: string | null;
  pdf_url: string;
  xls_url?: string | null;
  model: string;
  items: BOMItem[];
  components: Component[];
  manufacturers: Manufacturer[];
  metadata: PDFMetadata | null;
  features: any[];
  dimensions: Dimension[];
  total_pages: number;
  created_at: string;
  completed_at?: string | null;
}

export const loadExtraction = async (jobId: string): Promise<LoadExtractionResponse> => {
  try {
    const response = await api.get<LoadExtractionResponse>(`/api/extractions/load/${jobId}`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export interface FindExtractionByPdfResponse {
  success: boolean;
  exists: boolean;
  job_id?: string;
  pdf_filename?: string;
  xls_filename?: string;
  pdf_url?: string;
  xls_url?: string;
  model?: string;
  extraction_count?: number;
  total_candidates?: number;
  avg_confidence?: number;
  created_at?: string;
  completed_at?: string;
  message?: string;
}

export const findExtractionByPdf = async (pdfFilename: string): Promise<FindExtractionByPdfResponse> => {
  try {
    const response = await api.get<FindExtractionByPdfResponse>(`/api/extractions/find-by-pdf/${encodeURIComponent(pdfFilename)}`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const clearExtraction = async (jobId: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete<{ success: boolean; message: string }>(`/api/extractions/clear/${jobId}`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export interface CheckExtractionResponse {
  exists: boolean;
  job_id?: string;
  completed_at?: string | null;
  total_candidates?: number;
  has_extraction_data?: boolean;
}

export const checkExistingExtraction = async (
  pdfFilename: string,
  model: string = 'inference_engine'
): Promise<CheckExtractionResponse> => {
  try {
    const response = await api.get<CheckExtractionResponse>(
      `/api/extractions/check/${pdfFilename}`,
      { params: { model } }
    );
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const storeExtractionData = async (
  jobId: string,
  extractionData: {
    items: BOMItem[];
    components: Component[];
    manufacturers: Manufacturer[];
    metadata: PDFMetadata | null;
    features: any[];
    dimensions: Dimension[];
  }
): Promise<{ success: boolean }> => {
  try {
    const response = await api.post<{ success: boolean }>(
      `/api/extractions/store/${jobId}`,
      extractionData
    );
    return response.data;
  } catch (error) {
    // Fail silently - this is optional
    console.error('Failed to store extraction data:', error);
    return { success: false };
  }
};

// ============================================================================
// Extraction Rules APIs (Training System)
// ============================================================================

export interface RulesListResponse {
  success: boolean;
  rules: ExtractionRule[];
  total: number;
}

export const getRules = async (scope?: string): Promise<RulesListResponse> => {
  try {
    const params = scope ? { scope } : {};
    const response = await api.get<RulesListResponse>('/api/rules', { params });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const createRule = async (request: CreateRuleRequest): Promise<{
  success: boolean;
  rule: ExtractionRule;
  pattern: string;
  message: string;
}> => {
  try {
    const response = await api.post('/api/rules/create', request);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const updateRule = async (
  ruleId: string, 
  updates: Partial<ExtractionRule>
): Promise<{ success: boolean; rule: ExtractionRule }> => {
  try {
    // Use POST with JSON body to handle special characters like / in rule IDs
    const response = await api.post('/api/rules/update', {
      rule_id: ruleId,
      updates: updates
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const deleteRule = async (ruleId: string): Promise<{ success: boolean; message: string }> => {
  try {
    // Use POST with JSON body to handle special characters like / in rule IDs
    const response = await api.post('/api/rules/delete', {
      rule_id: ruleId
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const toggleRule = async (
  ruleId: string,
  enabled: boolean
): Promise<{ success: boolean; rule: ExtractionRule }> => {
  try {
    // Use POST with JSON body to handle special characters like / in rule IDs
    const response = await api.post('/api/rules/toggle', {
      rule_id: ruleId,
      enabled: enabled
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const teachPattern = async (request: {
  text: string;
  component_type: string;
  scope?: string;
  scope_value?: string;
}): Promise<{
  success: boolean;
  pattern: string;
  rule?: ExtractionRule;
  message: string;
}> => {
  try {
    const response = await api.post('/api/rules/teach', request);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export interface RuleTestResult {
  value: string;
  matched: boolean;
  action?: string;
  component_type?: string;
  error?: string;
}

export const testRule = async (
  ruleId: string, 
  testValues?: string[]
): Promise<{
  success: boolean;
  rule: ExtractionRule;
  results: RuleTestResult[];
  summary: { total: number; matched: number; not_matched: number };
}> => {
  try {
    // URL-encode the rule ID to handle special characters like /
    const encodedRuleId = encodeURIComponent(ruleId);
    const response = await api.post(`/api/rules/${encodedRuleId}/test`, {
      test_values: testValues || []
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

// ============================================================================
// Save Extraction API
// ============================================================================

export const saveExtraction = async (extractionData: {
  filename: string;
  main_bom: any[];
  auxiliary_bom: any[];
  stats: any;
  folder_id?: string | null;
}): Promise<{ success: boolean; message: string; job_id?: string; extraction_ids?: number[] }> => {
  try {
    const response = await api.post('/api/extractions/save', extractionData);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

// ============================================================================
// Delete File API
// ============================================================================

export const deleteFile = async (filename: string, fileType: 'pdf' | 'xls'): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.delete(`/api/files/delete/${fileType}/${encodeURIComponent(filename)}`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

// ============================================================================
// Investigate Missing Component API
// ============================================================================

export interface InvestigationResult {
  success: boolean;
  reference: string;
  filename: string;
  found_in_pdf: boolean;
  pdf_contexts: Array<{
    page: number;
    matched_as: string;
    context: string;
  }>;
  extraction_check: {
    test_line: string;
    extracted_refs: string[];
    reference_extracted: boolean;
  };
  filter_results: Array<{
    filter: string;
    passed: boolean;
    detail: string;
  }>;
  recommendation: 'NOT_IN_PDF' | 'NOT_EXTRACTED' | 'FILTERED_OUT' | 'SHOULD_BE_EXTRACTED';
  recommendation_text: string;
  suggested_rule?: {
    name: string;
    description: string;
    action: string;
    pattern: string;
    component_type: string;
    example: string;
    priority: string;
    override_exclusions?: boolean;
  };
}

// ============================================================================
// Job Notes API (PDF-level notes)
// ============================================================================

export const updateJobNotes = async (
  pdfFilename: string,
  notes: string | null
): Promise<{ success: boolean; job_id: string; pdf_filename: string; notes: string | null; message: string }> => {
  try {
    const response = await api.post('/api/job/notes', {
      pdf_filename: pdfFilename,
      notes: notes
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const getJobNotes = async (
  pdfFilename: string
): Promise<{ success: boolean; job_id?: string; pdf_filename: string; notes: string | null }> => {
  try {
    const response = await api.get('/api/job/notes', {
      params: { pdf_filename: pdfFilename }
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

// ============================================================================
// Job Lock Status API
// ============================================================================

export const updateJobLock = async (
  pdfFilename: string,
  isLocked: boolean
): Promise<{ success: boolean; job_id: string; pdf_filename: string; is_locked: boolean; message: string }> => {
  try {
    const response = await api.post('/api/job/lock', {
      pdf_filename: pdfFilename,
      is_locked: isLocked
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const getJobLock = async (
  pdfFilename: string
): Promise<{ success: boolean; job_id?: string; pdf_filename: string; is_locked: boolean }> => {
  try {
    const response = await api.get('/api/job/lock', {
      params: { pdf_filename: pdfFilename }
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

// ============================================================================
// Update Extraction Item API
// ============================================================================

export const updateExtractionComponentType = async (
  extractionId: number,
  componentType: string
): Promise<{ 
  success: boolean; 
  extraction_id: number; 
  old_component_type: string;
  new_component_type: string;
  message: string 
}> => {
  try {
    const response = await api.post('/api/extraction/update-component-type', {
      extraction_id: extractionId,
      component_type: componentType
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

// ============================================================================
// Extraction Notes API (DEPRECATED - kept for backward compatibility)
// ============================================================================

export const updateExtractionNotes = async (
  extractionId: number,
  notes: string | null
): Promise<{ success: boolean; extraction_id: number; notes: string | null; message: string }> => {
  try {
    const response = await api.post('/api/extraction/notes', {
      extraction_id: extractionId,
      notes: notes
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

// ============================================================================
// Send Extraction Summary Email
// ============================================================================

export const sendExtractionSummary = async (
  jobId: string,
  recipientEmails: string[]
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post('/api/extraction/send-summary', {
      job_id: jobId,
      recipient_emails: recipientEmails
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const investigateMissingComponent = async (
  reference: string,
  filename?: string
): Promise<InvestigationResult> => {
  try {
    const response = await api.post<InvestigationResult>('/api/rules/investigate', {
      reference,
      filename: filename || ''
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const createForceIncludeRule = async (request: {
  reference: string;
  component_type?: string;
  description?: string;
  scope?: string;
  scope_value?: string;  // folder_id for 'folder' scope
}): Promise<{
  success: boolean;
  rule: ExtractionRule;
  pattern: string;
  message: string;
}> => {
  try {
    const response = await api.post('/api/rules/force-include', request);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export const generateRuleWithLLM = async (request: {
  reference: string;
  filename?: string;
  extraction_examples?: Array<{
    reference: string;
    component_type?: string;
    description?: string;
    context?: string;
  }>;
}): Promise<{
  success: boolean;
  rule: ExtractionRule;
  pattern: string;
  reasoning: string;
  investigation: InvestigationResult;
  message: string;
}> => {
  try {
    const response = await api.post('/api/rules/generate-with-llm', request);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * Apply a rule incrementally to an existing extraction WITHOUT losing verification status.
 * This is the key feature for "Rule Scope Limiting".
 */
export const applyRuleIncremental = async (request: {
  rule_id: string;
  job_id: string;
  pdf_path?: string;
}): Promise<{
  success: boolean;
  message: string;
  items_added: number;
  items_removed: number;
  items_unchanged: number;
  new_items: Array<{
    reference: string;
    page: number;
    component_type: string;
  }>;
  rule_applied: string;
}> => {
  try {
    const response = await api.post('/api/rules/apply-incremental', request);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * Merge verification status from a previous extraction into a new one.
 * Preserves validation work for items that haven't changed.
 */
export const mergeVerificationStatus = async (request: {
  old_job_id: string;
  new_job_id: string;
}): Promise<{
  success: boolean;
  message: string;
  merged_count: number;
  new_count: number;
  total_items: number;
}> => {
  try {
    const response = await api.post('/api/extractions/merge-verification', request);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

// ============================================================================
// Reconciliation APIs
// ============================================================================

export interface ReconciliationMatch {
  id: number;
  extracted_tag: string;
  extracted_description: string | null;
  component_type: string | null;
  pages: string | null;
  customer_part_number: string | null;
  customer_supplier_part: string | null;
  erp_pp_number: string | null;
  erp_description: string | null;
  erp_manufacturer_code: string | null;
  erp_item_category: string | null;
  confidence: number;
  match_sources: Record<string, boolean>;
  status: 'pending' | 'auto_accepted' | 'needs_review' | 'no_match' | 'confirmed' | 'rejected';
  auto_accepted: boolean;
  user_confirmed: boolean;
  user_notes: string | null;
}

export interface UntaggedBOMItem {
  id: number;
  customer_part_number: string | null;
  supplier_part_number: string | null;
  description: string | null;
  quantity: number;
  device_tag: string | null;
}

export interface ReconciliationResults {
  success: boolean;
  matches: ReconciliationMatch[];
  untagged_items: UntaggedBOMItem[];
  stats: {
    total_matches: number;
    auto_accepted: number;
    needs_review: number;
    no_match: number;
    confirmed: number;
    untagged_bom_items: number;
  };
}

export interface BOMUpload {
  id: string;
  filename: string;
  job_id: string | null;
  folder_id: string | null;
  total_items: number;
  matched_items: number;
  created_at: string | null;
}

export interface CrossRefItem {
  id: number;
  customer_ref: string;
  manufacturer_name: string | null;
  manufacturer_part_ref: string | null;
  description: string | null;
  erp_part_number: string | null;
  source_file: string | null;
}

/**
 * Preview BOM file columns for mapping
 */
export const previewBOMColumns = async (file: File): Promise<{
  success: boolean;
  columns: string[];
  preview_rows: Record<string, string>[];
  total_rows: number;
  error?: string;
}> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/api/reconciliation/preview-bom', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * Upload customer BOM for reconciliation
 */
export const uploadCustomerBOM = async (
  file: File,
  columnMapping: Record<string, string>,
  jobId?: string,
  folderId?: string
): Promise<{
  success: boolean;
  upload_id: string;
  filename: string;
  total_items: number;
  error?: string;
}> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('column_mapping', JSON.stringify(columnMapping));
    if (jobId) formData.append('job_id', jobId);
    if (folderId) formData.append('folder_id', folderId);
    
    const response = await api.post('/api/reconciliation/upload-bom', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * Upload customer cross-reference file for a folder
 */
export const uploadCrossReference = async (
  file: File,
  folderId: string,
  columnMapping?: Record<string, string>
): Promise<{
  success: boolean;
  folder_id: string;
  filename: string;
  items_imported: number;
  error?: string;
}> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder_id', folderId);
    if (columnMapping) {
      formData.append('column_mapping', JSON.stringify(columnMapping));
    }
    
    const response = await api.post('/api/reconciliation/upload-crossref', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * Get cross-references for a folder
 */
export const getFolderCrossRefs = async (folderId: string): Promise<{
  success: boolean;
  folder_id: string;
  count: number;
  items: CrossRefItem[];
}> => {
  try {
    const response = await api.get(`/api/reconciliation/crossref/${folderId}`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * Run tag-led reconciliation for a job
 */
export const runReconciliation = async (
  jobId: string,
  bomUploadId?: string
): Promise<{
  success: boolean;
  job_id: string;
  stats: {
    total: number;
    auto_accepted: number;
    needs_review: number;
    no_match: number;
  };
  error?: string;
}> => {
  try {
    const response = await api.post('/api/reconciliation/run', {
      job_id: jobId,
      bom_upload_id: bomUploadId,
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * Get lightweight reconciliation stats for a job (for history list)
 */
export const getReconciliationStats = async (jobId: string): Promise<{
  success: boolean;
  stats?: {
    total_matches: number;
    auto_accepted: number;
    needs_review: number;
    no_match: number;
    confirmed: number;
    untagged_bom_items: number;
  };
  error?: string;
}> => {
  try {
    const response = await api.get(`/api/reconciliation/stats/${jobId}`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * Get reconciliation results for a job
 */
export const getReconciliationResults = async (jobId: string): Promise<ReconciliationResults> => {
  try {
    const response = await api.get(`/api/reconciliation/results/${jobId}`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * Delete reconciliation data for a job
 */
export const deleteReconciliation = async (jobId: string): Promise<{
  success: boolean;
  message?: string;
  deleted_count?: number;
  error?: string;
}> => {
  try {
    const response = await api.delete(`/api/reconciliation/${jobId}`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * Confirm a reconciliation match
 */
export const confirmReconciliationMatch = async (
  matchId: number,
  notes?: string,
  confirmedBy?: string
): Promise<{ success: boolean }> => {
  try {
    const response = await api.post(`/api/reconciliation/match/${matchId}/confirm`, {
      notes,
      confirmed_by: confirmedBy,
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * Reject a reconciliation match
 */
export const rejectReconciliationMatch = async (
  matchId: number,
  notes?: string
): Promise<{ success: boolean }> => {
  try {
    const response = await api.post(`/api/reconciliation/match/${matchId}/reject`, { notes });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * Manually assign ERP part to a match
 */
export const assignERPToMatch = async (
  matchId: number,
  erpPartId: number,
  confirmedBy?: string
): Promise<{ success: boolean }> => {
  try {
    const response = await api.post(`/api/reconciliation/match/${matchId}/assign-erp`, {
      erp_part_id: erpPartId,
      confirmed_by: confirmedBy,
    });
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * List BOM uploads
 */
export const listBOMUploads = async (
  jobId?: string,
  folderId?: string
): Promise<{
  success: boolean;
  uploads: BOMUpload[];
}> => {
  try {
    const params = new URLSearchParams();
    if (jobId) params.append('job_id', jobId);
    if (folderId) params.append('folder_id', folderId);
    
    const response = await api.get(`/api/reconciliation/bom-uploads?${params.toString()}`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * Get items from a BOM upload
 */
export const getBOMUploadItems = async (uploadId: string): Promise<{
  success: boolean;
  upload_id: string;
  count: number;
  items: Array<{
    id: number;
    device_tag: string | null;
    customer_part_number: string | null;
    supplier_part_number: string | null;
    description: string | null;
    quantity: number;
    match_status: string;
    match_confidence: number | null;
  }>;
}> => {
  try {
    const response = await api.get(`/api/reconciliation/bom-upload/${uploadId}/items`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

/**
 * Delete a BOM upload and all its items
 */
export const deleteBOMUpload = async (uploadId: number): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> => {
  try {
    const response = await api.delete(`/api/reconciliation/bom-upload/${uploadId}`);
    return response.data;
  } catch (error) {
    return handleApiError(error as AxiosError<{ message?: string }>);
  }
};

export default api;

